import React from 'react';
import { motion } from 'framer-motion';
import { FaTimes, FaCheck, FaTag, FaExternalLinkAlt } from 'react-icons/fa';

function getCommissionSource(campaign) {
    return campaign?.agreement?.commission || campaign?.commission || null;
}

function formatCommissionLine(campaign) {
    const c = getCommissionSource(campaign);
    if (!c) return '—';
    const sym = c.currency?.symbol || '₹';
    if (c.model === 'fixed' && c.amount != null && c.amount !== '') {
        return `${sym}${Number(c.amount).toLocaleString('en-IN')} fixed`;
    }
    if (c.model === 'percentage' && c.percentage != null) {
        return `${c.percentage}%`;
    }
    return '—';
}

function commissionModelLabel(campaign) {
    const c = getCommissionSource(campaign);
    if (!c?.model) return '—';
    if (c.model === 'fixed') return 'Fixed';
    if (c.model === 'percentage') return 'Percentage';
    return String(c.model);
}

function inferDeliverablesLine(campaign) {
    const raw = String(campaign?.description || '').trim();
    const m = raw.match(/(\d+)\s*(reels?|stories?|posts?)/i);
    if (m) {
        const n = m[1];
        let kind = m[2].toLowerCase();
        if (kind === 'reel') kind = 'Reels';
        else if (kind === 'story') kind = 'Stories';
        else if (kind === 'stories') kind = 'Stories';
        else if (kind === 'post') kind = 'Posts';
        else if (kind === 'posts') kind = 'Posts';
        else kind = m[2];
        return `${n} ${kind}`;
    }
    return 'As specified in brief';
}

function formatDurationRange(campaign) {
    const start = campaign?.startDate ? new Date(campaign.startDate) : null;
    const end = campaign?.expiresAt ? new Date(campaign.expiresAt) : null;
    const ok = (d) => d && !Number.isNaN(d.getTime());
    if (!ok(start) && !ok(end)) return '—';
    const opts = { month: 'numeric', day: 'numeric', year: 'numeric' };
    const a = ok(start) ? start.toLocaleDateString('en-US', opts) : '—';
    const b = ok(end) ? end.toLocaleDateString('en-US', opts) : '—';
    return `${a} – ${b}`;
}

export function CampaignBriefModal({
    isOpen,
    onClose,
    campaign,
    companyName,
    brandName,
    companyEmail,
    companyPhone,
    onAccept,
    onDecline,
}) {
    if (!isOpen || !campaign) return null;

    const commissionLine = formatCommissionLine(campaign);
    const deliverablesLine = inferDeliverablesLine(campaign);
    const typeBadge = commissionModelLabel(campaign);
    const durationLine = formatDurationRange(campaign);
    const briefText = (campaign.description || '').trim() || 'No brief details were provided for this campaign.';

    return (
        <div
            className="fixed inset-0 z-[130] flex items-center justify-center p-3 sm:p-4 bg-black/50"
            role="dialog"
            aria-modal="true"
            aria-labelledby="campaign-brief-title"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
        >
            <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 8 }}
                transition={{ duration: 0.18 }}
                className="w-full max-w-[min(100%,28rem)] sm:max-w-xl lg:max-w-2xl max-h-[min(calc(100dvh-5.25rem),880px)] lg:max-h-[min(calc(100dvh-2.25rem),880px)] flex flex-col min-h-0 bg-white rounded-xl sm:rounded-2xl border border-gray-200 shadow-2xl overflow-hidden"
            >
                {/* Header */}
                <div className="flex items-start justify-between gap-3 px-3 py-2 sm:px-4 sm:py-3 border-b border-gray-100 shrink-0">
                    <h2
                        id="campaign-brief-title"
                        className="text-base sm:text-lg lg:text-xl font-bold text-gray-900 leading-snug pr-2 min-w-0"
                    >
                        {campaign.name}
                    </h2>
                    <button
                        type="button"
                        onClick={onClose}
                        className="shrink-0 w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:text-gray-800 hover:bg-gray-50 transition-colors"
                        aria-label="Close"
                    >
                        <FaTimes className="text-sm sm:text-base" />
                    </button>
                </div>

                <div className="min-h-0 overflow-y-auto flex-1 px-4 py-3 sm:px-5 sm:py-4 space-y-3 sm:space-y-4">
                    {/* Identity strip */}
                    <div className="flex items-center gap-3 rounded-xl bg-[#F3F4F6] border border-gray-100/80 px-3 py-2.5 sm:px-4 sm:py-3">
                        <div
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg flex items-center justify-center shrink-0 bg-[#FFE9DB] text-[#C2410C] border border-orange-100"
                            aria-hidden
                        >
                            <FaTag className="text-base sm:text-lg" />
                        </div>
                        <div className="min-w-0">
                            <p className="text-xs sm:text-sm lg:text-lg font-bold text-gray-900 truncate">{campaign.name}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 truncate mt-0.5">{brandName || companyName || 'Brand'}</p>
                        </div>
                    </div>

                    {/* 2×2 grid */}
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <div className="rounded-lg bg-[#F3F4F6] border border-gray-100/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                            <p className="text-xs md:text-sm font-semibold text-gray-900 mb-1">Commission</p>
                            <p className="text-[10px] md:text-sm font-medium text-gray-500 leading-tight break-words">
                                {commissionLine}
                            </p>
                        </div>
                        <div className="rounded-lg bg-[#F3F4F6] border border-gray-100/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                            <p className="text-xs md:text-sm font-semibold text-gray-900 mb-1">Deliverables</p>
                            <p className="text-[10px] md:text-sm font-medium text-gray-500 leading-tight break-words">
                                {deliverablesLine}
                            </p>
                        </div>
                        <div className="rounded-lg bg-[#F3F4F6] border border-gray-100/80 px-2.5 py-2 sm:px-3 sm:py-2.5 flex flex-col justify-center">
                            <p className="text-xs md:text-sm font-semibold text-gray-900 mb-1">Type</p>
                            <span className="inline-flex w-fit items-center rounded-md bg-blue-50 px-2 py-0.5 text-[10px] md:text-sm font-medium text-blue-600 border border-blue-100/80">
                                {typeBadge}
                            </span>
                        </div>
                        <div className="rounded-lg bg-[#F3F4F6] border border-gray-100/80 px-2.5 py-2 sm:px-3 sm:py-2.5">
                            <p className="text-xs md:text-sm font-semibold text-gray-900 mb-1">Duration</p>
                            <p className="text-[10px] md:text-sm font-medium text-gray-500 leading-tight break-words">{durationLine}</p>
                        </div>
                    </div>

                    {/* Campaign type (Advertising / Affiliate / Hybrid) */}
                    {campaign.campaignType ? (
                        <div className="flex flex-wrap items-center gap-2 text-[10px] sm:text-xs text-gray-600">
                            <span className="text-gray-500 font-medium">Campaign type</span>
                            <span className="font-semibold text-gray-800">{campaign.campaignType}</span>
                        </div>
                    ) : null}

                    {/* Brand */}
                    <div>
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500 mb-1.5">Brand</p>
                        <div className="text-xs sm:text-sm lg:text-base text-gray-900 space-y-1">
                            <p className="font-semibold">{brandName || '—'}</p>
                            {companyName ? <p className="text-gray-700">Company: {companyName}</p> : null}
                            {companyEmail && String(companyEmail).trim() ? (
                                <p className="text-gray-600 break-all">Email: {companyEmail}</p>
                            ) : null}
                            {companyPhone && String(companyPhone).trim() ? (
                                <p className="text-gray-600">Phone: {companyPhone}</p>
                            ) : null}
                        </div>
                    </div>

                    {/* Brief */}
                    <div>
                        <p className="text-[10px] sm:text-xs font-medium text-gray-500 mb-1.5">Brief</p>
                        <div className="rounded-lg bg-[#F3F4F6] border border-gray-100/80 px-3 py-2.5 sm:px-3.5 sm:py-3">
                            <p className="text-xs sm:text-sm lg:text-base text-gray-800 whitespace-pre-wrap leading-relaxed">
                                {briefText}
                            </p>
                        </div>
                    </div>

                    {campaign.landingPage ? (
                        <a
                            href={campaign.landingPage}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700"
                        >
                            <FaExternalLinkAlt className="text-[10px] sm:text-xs shrink-0" />
                            Open original brief link
                        </a>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="shrink-0 border-t border-gray-100 bg-white px-4 py-3 sm:px-5 sm:py-3.5 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="inline-flex items-center justify-center md:px-3 md:py-2 px-2 py-1 rounded-lg border border-gray-300 bg-white text-gray-700 text-xs sm:text-sm md:font-medium font-normal hover:bg-gray-50 transition-colors"
                    >
                        Close
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onDecline?.();
                            onClose();
                        }}
                        className="inline-flex items-center justify-center md:gap-1.5 gap-1 md:px-3 md:py-2 px-2 py-1 rounded-lg bg-[#dc2626] hover:bg-[#b91c1c] text-white text-xs sm:text-sm md:font-semibold font-medium transition-colors shadow-sm"
                    >
                        <FaTimes className="text-[8px] sm:text-xs md:text-xs shrink-0" />
                        Decline
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            onAccept?.();
                            onClose();
                        }}
                        className="inline-flex items-center justify-center md:gap-1.5 gap-1 md:px-3 md:py-2 px-2 py-1 rounded-lg bg-[#16a34a] hover:bg-[#15803d] text-white text-xs sm:text-sm md:font-semibold font-medium transition-colors shadow-sm"
                    >
                        <FaCheck className="text-[8px] sm:text-xs md:text-xs shrink-0" />
                        Accept campaign
                    </button>
                </div>
            </motion.div>
        </div>
    );
}
